import { message, Spin } from 'ant-design-vue';
import { ref, computed, nextTick, createApp } from 'vue';
import { createI18n } from 'vue-i18n';
import axios from 'axios';
import * as $math from 'mathjs';

/**
 * @description 根据key值给指定属性赋值
 * @param list list
 * @param key key值
 * @param attr 属性名
 * @param value 赋值
 */
const setlistValue = (list, key, attr, value) => {
    const index = list.findIndex((item) => item.key == key);
    if (index > -1) {
        if (attr.indexOf('.') > -1) {
            const arr = attr.split('.');
            const obj = arr[0];
            const objAttr = arr[1];
            list[index][obj][objAttr] = value;
        }
        else {
            list[index][attr] = value;
        }
    }
};

// 复制文本内容时去掉文本两端空格
const copyListener = (e) => {
    // 如果 未复制或者未剪切，直接 return
    if (!e.clipboardData)
        return;
    // Selection 对象 表示用户选择的文本范围或光标的当前位置。
    // 声明一个变量接收 -- 用户输入的剪切或者复制的文本转化为字符串
    const selection = window.getSelection();
    const text = selection ? selection.toString() : '';
    if (text) {
        // 如果文本存在，首先取消默认行为
        e.preventDefault();
        e.clipboardData.setData('text/plain', text.trim());
    }
};
// 粘贴文本内容时去掉文本两端空格
const pasteListener = (e) => {
    const clipText = e.clipboardData.getData('Text');
    if (clipText) {
        e.clipboardData.setData('Text', clipText.trim());
        setTimeout(() => {
            e.target.value = e.target.value.trim();
        });
    }
};
const copy = (value) => {
    const input = document.createElement('textarea');
    document.body.appendChild(input);
    input.value = value;
    input.select();
    document.execCommand('Copy');
    message.success('已复制到剪贴板');
    document.body.removeChild(input);
};
// 点击按钮复制
const clickCopyListener = (ev) => {
    if (ev.target.classList &&
        ev.target.classList.value.indexOf('copy-icon') != -1) {
        const text = ev.target.previousElementSibling.innerText;
        copy(text);
        ev.stopPropagation();
    }
};
// 点击按钮复制
const clickCopyListenerNew = (ev) => {
    if (ev.target.classList &&
        ev.target.classList.value.indexOf('copy-auto') != -1) {
        const { x, width } = ev.target.getBoundingClientRect();
        const left = Math.floor(x) + width - ev.screenX;
        // 点击复制icon区域
        if (left <= 17) {
            const text = ev.target.innerText;
            copy(text);
            ev.preventDefault();
            ev.stopPropagation();
            return false;
        }
    }
};
const globalEventListener = () => {
    document.addEventListener('copy', copyListener);
    document.addEventListener('paste', pasteListener);
    document.addEventListener('click', clickCopyListener);
    document.addEventListener('click', clickCopyListenerNew);
};

const useInnerGrid = (columnDefs, visible, value, indexField, isMultiSelect, context) => {
    const gridRef = ref();
    const selectedValueRef = ref(JSON.parse(JSON.stringify(value.value)));
    // 弹框是否显示
    const visibleRef = computed({
        get: () => visible.value,
        set: (val) => {
            context.emit('update:visible', val);
        },
    });
    // 表格初始设置
    const gridDefs = {
        getRowNodeId: (data) => data[indexField],
    };
    // 单选模式和多选模式
    const multiSelectModel = () => {
        if (!isMultiSelect) {
            // 单选模式
            gridDefs.rowMultiSelectWithClick = false;
            // columnDefs.splice(0, 1);
        }
        else {
            // 多选模式
            columnDefs.splice(0, 1, {
                field: '',
                headerCheckboxSelection: true,
                checkboxSelection: true,
            });
        }
    };
    multiSelectModel();
    // 表格初始选择
    const onRowDataChanged = (params) => {
        selectedValueRef.value.map((data) => {
            const node = params.api.getRowNode(data[indexField]);
            node && node.setSelected(true);
        });
    };
    const onRowSelected = (params) => {
        const { data, node } = params;
        const selected = node.isSelected();
        const index = selectedValueRef.value.findIndex((e) => e[indexField] === node.id);
        if (selected) {
            if (isMultiSelect) {
                index === -1 && selectedValueRef.value.push(data);
            }
            else {
                selectedValueRef.value.splice(0);
                selectedValueRef.value.push({ ...data, closable: false });
            }
        }
        else {
            // 禁止删除closable:false
            if (isMultiSelect) {
                if (index !== -1 && selectedValueRef.value[index].closable !== false) {
                    selectedValueRef.value.splice(index, 1);
                }
                else {
                    node.setSelected(true);
                    message.warn('默认值禁止删除');
                }
            }
            else {
                index !== -1 && selectedValueRef.value.splice(index, 1);
            }
        }
        // context.emit('update:value', selectedValueRef.value);
    };
    const onClose = (nodeId) => {
        // 表格取消选择
        const node = gridRef.value.gridApi.getRowNode(nodeId);
        node && node.setSelected(false);
        // context.emit('update:value', selectedValueRef.value);
    };
    // 提交
    const handleOk = () => {
        context.emit('submit', selectedValueRef.value);
        visibleRef.value = false;
    };
    return {
        gridDefs,
        columnDefs,
        onRowSelected,
        selectedValueRef,
        onClose,
        gridRef,
        visibleRef,
        indexField,
        onRowDataChanged,
        handleOk,
    };
};

const I18N_LOCALE_KEY = 'SKYCORE_I18N_LOCALE';
const languageList = [
    { key: 'zh-CN', name: '简体中文', message: {} },
    { key: 'en-US', name: 'English', message: {} },
];
const fallbackLocale = languageList[0].key;
const modules = import.meta.glob(['../**/zh-CN.ts', '../**/en-US.ts']);
Object.keys(modules).forEach((modulePath) => {
    // eslint-disable-next-line
    modules[modulePath]().then((module) => {
        const messageData = module.default;
        const matchedKey = modulePath
            .split('/')
            .reverse()[0]
            .replace('.ts', '');
        const matchedLang = languageList.find((lang) => lang.key === matchedKey);
        if (matchedLang) {
            Object.assign(matchedLang.message, messageData);
        }
    });
});
const storedLocale = localStorage.getItem(I18N_LOCALE_KEY);
const matchedStoredLocale = storedLocale && languageList.map((lang) => lang.key).includes(storedLocale)
    ? storedLocale
    : '';
const presetLocale = matchedStoredLocale || navigator.language || fallbackLocale;
const messages = Object.fromEntries(languageList.map(({ key, message }) => [key, message]));
const i18n = createI18n({
    locale: presetLocale,
    messages,
    fallbackLocale,
});

/**
 * @description 获取 url 查询参数
 * @param key? 参数名，可选，
 * @return string | object | number | undefined , key 不传则返回包含所有参数的对象
 */
const getQuery = (key) => {
    const result = {};
    const search = decodeURIComponent(window.location.search);
    const tempArr = search && search.indexOf('?') === 0 ? search.split('?')[1].split('&') : [];
    tempArr.forEach((item) => {
        if (item) {
            const itemArr = item.split('=');
            // 临时数据类型转换
            const isNumber = itemArr[1].length < 11 && /^\d+$/.test(itemArr[1]);
            result[itemArr[0]] = isNumber ? parseInt(itemArr[1], 10) : itemArr[1];
        }
    });
    return key ? result[key] : result;
};

const { global: { t }, } = i18n;
message.config({ maxCount: 1 });
let requestCount = 0;
const prevent = (e) => e.stopImmediatePropagation();
const loading = () => {
    if (requestCount === 0) {
        nextTick().then(() => {
            const dom = document.createElement('div');
            dom.setAttribute('id', 'loading');
            dom.addEventListener('mousedown', prevent);
            dom.addEventListener('touchstart', prevent);
            document.body.appendChild(dom);
            createApp(Spin).mount(dom);
        });
    }
    requestCount += 1;
};
const hideLoading = () => {
    requestCount -= 1;
    const loadingDom = document.getElementById('loading');
    if (requestCount === 0 && loadingDom) {
        document.body.removeChild(loadingDom);
    }
};
const isDev = import.meta.env.DEV;
const env = getQuery('proxy') || import.meta.env.VITE_API_ENV;
// 根据 env 参数切换接口环境，仅在开发环境生效；proxy 保留参数，不要在业务接口中使用
const envSet = new Set(['mock', 'dev', 'qa', 'pro']);
const envUrlDic = {
    mock: '/mock',
    dev: '/dev',
    qa: '/qa',
    pro: '/pro',
};
const getBaseUrl = (config = {}) => {
    let baseURL = '';
    if (isDev) {
        const envInner = env || config.proxyEnv || 'mock';
        if (envInner && envSet.has(envInner)) {
            // TODO 地址填充
            baseURL = envUrlDic[envInner];
        }
    }
    else if (process.env.NODE_ENV === 'production' && process.env.CLIENT === 'electron') {
        let baseURLInner = '';
        if ((config.url || '').startsWith('/ram'))
            baseURLInner = '/1713234-0-default';
        if ((config.url || '').startsWith('/pdm'))
            baseURLInner = '/1783640-0-default';
        if ((config.url || '').startsWith('/common'))
            baseURLInner = '/1963470-0-default';
        if ((config.url || '').startsWith('/oms'))
            baseURLInner = '/2076887-0-default';
        if ((config.url || '').startsWith('/crm'))
            baseURLInner = '/2186502-0-default';
        if ((config.url || '').startsWith('/pms'))
            baseURLInner = '/2373963-0-default';
        baseURL = import.meta.env.VITE_API_PREFIX + baseURLInner;
    }
    return baseURL;
};
const triggerDownload = (url, filename = '') => {
    const link = document.createElement('a');
    link.style.display = 'none';
    // a 标签的 download 属性就是下载下来的文件名
    link.download = filename;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
const commonHeader = () => ({
    'x-requested-with': 'XMLHttpRequest',
    Authorization: localStorage.getItem('Authorization') || false,
});
/**
 * 请求
 * @param isDownload 只有响应类型是Blob时使用，true: 下载 false: 预览 undefined(不传): 按常规接口处理
 */
const http = (config, isDownload) => {
    return new Promise((resolve, reject) => {
        config.validateStatus = status => {
            return status < 500;
        };
        config.headers = {
            ...commonHeader(),
            ...config.headers,
        };
        config.baseURL = getBaseUrl(config);
        if (config.proxyEnv) {
            delete config.proxyEnv;
        }
        if (!config.method)
            config.method = 'post';
        if (config.loading !== false) {
            loading();
        }
        if (isDownload !== undefined) {
            config.responseType = 'blob';
        }
        axios(config)
            .then((response) => {
            const { headers } = response;
            const res = response.data;
            if (response.status === 200) {
                // mock 不校验 code
                if (env === 'mock') {
                    resolve(res.data);
                    return;
                }
                if (isDownload !== undefined && res instanceof Blob) {
                    const mimeType = headers['content-type'] ?? 'application/pdf';
                    const objectURL = window.URL.createObjectURL(new Blob([res], { type: mimeType }));
                    if (isDownload) {
                        const filename = decodeURIComponent(headers['content-disposition']?.split(';')[1].split('=')[1]);
                        triggerDownload(objectURL, filename);
                    }
                    else {
                        window.open(objectURL);
                    }
                    // 释放的 URL 对象以及移除 a 标签
                    URL.revokeObjectURL(objectURL);
                    resolve(res);
                    return;
                }
                if (typeof res === 'object') {
                    if (res.code === 200) {
                        resolve(res.data);
                        return;
                    }
                    // 未登录或登录信息失效
                    if (res.code === 10203001 || res.code === 10203003) {
                        let params = '';
                        if (res?.data?.isRedirectToOriginalPage) {
                            params = `?redirectUrl=${encodeURIComponent(window.location.href)}`;
                        }
                        window.location.replace(`/#/login${params}`);
                        window.location.reload();
                        return;
                    }
                }
            }
            if (headers.redirect_url) {
                window.location.href = headers.redirect_url;
            }
            const errorRes = {
                message: t('message.commonRequestFailed'),
                ...(res || {}),
            };
            message.error(errorRes.message);
            reject(response);
        })
            .catch(response => {
            message.error(response?.data?.message || t('message.commonRequestFailed'));
        })
            .finally(() => {
            if (config.loading !== false) {
                hideLoading();
            }
        });
    });
};

const pathname = window.location.pathname || '';
// =================上面的方法废弃了==================
// 获取本地存储大小
const localStorageSize = function () {
    let lsTotal = 0, xLen, x;
    for (x in localStorage) {
        // eslint-disable-next-line no-prototype-builtins
        if (!window.localStorage.hasOwnProperty(x))
            continue;
        xLen = (localStorage[x].length + x.length) * 2;
        lsTotal += xLen;
    }
    return lsTotal / 1024 / 1024;
};
// 获取当前用户
const getUserInfo = () => JSON.parse(localStorage.userInfo || '{}');
const getStateItemKey = () => {
    const { organizationId, userId } = getUserInfo();
    return `${String(organizationId || 0)}+${String(userId || 0)}`;
};
// 缓存数据
function setState(name, data) {
    if (name.indexOf('/') === -1)
        name = pathname + name;
    let m = new Map();
    const k = getStateItemKey();
    // 先查询
    const localData = localStorage.getItem(name);
    if (localData) {
        const localDataParsed = JSON.parse(localData);
        if (Array.isArray(localDataParsed)) {
            m = new Map(localDataParsed);
            // 如有删除，再插入
            if (m.has(k)) {
                m.delete(k);
            }
            // 检查占用，删除最早的
            if (localStorageSize() > 4) {
                m.delete(m.keys().next().value);
            }
        }
    }
    m.set(k, data);
    return localStorage.setItem(name, JSON.stringify([...m]));
}
// 获取缓存数据
function getState(name) {
    if (name.indexOf('/') === -1)
        name = pathname + name;
    const localData = localStorage.getItem(name);
    if (localData) {
        let m = new Map();
        const k = getStateItemKey();
        // 先查询
        if (localData) {
            const localDataParsed = JSON.parse(localData);
            if (Array.isArray(localDataParsed)) {
                m = new Map(localDataParsed);
                if (m.has(k))
                    return m.get(k);
                return '';
            }
        }
    }
    return localData ? JSON.parse(localData) : '';
}
// 清除缓存数据
function removeState(name) {
    if (name.indexOf('/') === -1)
        name = pathname + name;
    return localStorage.removeItem(name);
}
/**
 * @description 获取query
 */
const getQueryKeywords = () => {
    const result = {};
    const search = decodeURIComponent(window.location.search);
    const tempArr = search && search.indexOf('?') === 0 ? search.split('?')[1].split('&') : [];
    tempArr.forEach((item) => {
        if (item) {
            const itemArr = item.split('=');
            // 临时数据类型转换
            const isnum = itemArr[1].length < 11 && /^\d+$/.test(itemArr[1]);
            result[itemArr[0]] = isnum ? parseInt(itemArr[1]) : itemArr[1];
        }
    });
    return result;
};

const math = {
    /**
     * 乘法
     * num1 * num2
     * */
    accMul(num1, num2) {
        if (num1 === null || num2 === null)
            return null;
        num1 = Number(num1);
        num2 = Number(num2);
        if (isNaN(num1) || isNaN(num2))
            return null;
        return Number($math.multiply($math.bignumber(num1), $math.bignumber(num2)));
    },
    /**
     * 除法
     * num1 ÷ num2
     * */
    accDiv(num1, num2) {
        if (num1 === null || num2 === null)
            return null;
        num1 = Number(num1);
        num2 = Number(num2);
        if (isNaN(num1) || isNaN(num2))
            return null;
        return Number($math.divide($math.bignumber(num1), $math.bignumber(num2)));
    },
    /**
     * 加法
     * num1 + num2
     * */
    accAdd(num1, num2) {
        if (num1 === null || num2 === null)
            return null;
        num1 = Number(num1);
        num2 = Number(num2);
        if (isNaN(num1) || isNaN(num2))
            return null;
        return Number($math.add($math.bignumber(num1), $math.bignumber(num2)));
    },
    /**
     * 减法
     * num1 - num2
     * */
    accSub(num1, num2) {
        if (num1 === null || num2 === null)
            return null;
        num1 = Number(num1);
        num2 = Number(num2);
        if (isNaN(num1) || isNaN(num2))
            return null;
        return Number($math.subtract($math.bignumber(num1), $math.bignumber(num2)));
    },
    /**
     * 只入不舍(进一)
     * @param num
     * @param cent
     * @returns
     */
    ceil(num, cent) {
        if (num === null || isNaN(Number(num)))
            return 0;
        cent =
            cent == undefined
                ? num.toLocaleString().split('.')[1]
                    ? num.toLocaleString().split('.')[1].length
                    : 0
                : cent;
        num = Number(num);
        if (cent == 0) {
            return Number($math.ceil(num));
        }
        const powCent = $math.pow(10, cent);
        const n = Number($math.multiply(num, powCent));
        return Number($math.divide($math.ceil(n), powCent));
    },
    /**
     * 只舍不入
     * @param num
     * @param cent
     * @returns
     */
    floor(num, cent) {
        if (num === null || isNaN(Number(num)))
            return 0;
        cent =
            cent == undefined
                ? num.toLocaleString().split('.')[1]
                    ? num.toLocaleString().split('.')[1].length
                    : 0
                : cent;
        num = Number(num);
        if (cent == 0) {
            return Number($math.floor(num));
        }
        const powCent = $math.pow(10, cent);
        num = Number(num);
        const n = Number($math.multiply(num, powCent));
        return Number($math.divide($math.floor(n), powCent));
    },
    /**
     * 四舍五入
     * @param num
     * @param cent
     * @returns
     */
    round(num, cent) {
        if (num === null || isNaN(Number(num)))
            return 0;
        cent =
            cent == undefined
                ? num.toLocaleString().split('.')[1]
                    ? num.toLocaleString().split('.')[1].length
                    : 0
                : cent;
        num = Number(num);
        if (cent == 0) {
            return Number($math.round(num));
        }
        const powCent = $math.pow(10, cent);
        num = Number(num);
        const n = Number($math.multiply(num, powCent));
        return Number($math.divide($math.round(n), powCent));
    },
    /**
     * Correct the given number to specifying significant digits.
     *
     * @param num The input number
     * @param precision An integer specifying the number of significant digits
     *
     * @example strip(0.09999999999999998) === 0.1 // true
     */
    strip(num, precision = 15) {
        return +parseFloat(Number(num).toPrecision(precision));
    },
    /**
     * 千分制格式化方法 (无需千分制格式化可直接使用mathjs中的方法 如：$math.ceil(0.4555, 2) // result: 0.46)
     * num: 数值
     * cent: 保留位数
     * type: 取值方法 - ceil: 有舍必入(进一)，floor: 只舍不入，round: 四舍五入 ...
     *      (如需更多方法可参考mathjs文档:  https://mathjs.org/docs/reference/functions.html)
     * */
    toThousands(params) {
        params = Object.assign({
            num: 0,
            type: 'ceil',
            isThousand: true,
        }, params);
        let num = params.num;
        if (num === null || isNaN(Number(num)))
            return null;
        num = this.strip(params.num);
        const cent = params.cent == undefined
            ? num.toLocaleString().split('.')[1]
                ? num.toLocaleString().split('.')[1].length
                : 0
            : params.cent;
        num = Number(num);
        switch (params.type) {
            case 'ceil':
                num = math.ceil(num, cent);
                break;
            case 'floor':
                num = math.floor(num, cent);
                break;
            case 'round':
                num = $math.round(num, cent);
                break;
        }
        let num1 = '';
        let sign = '';
        if (num < 0) {
            sign = '-';
            num = -num;
        }
        if (params.isThousand && num >= 1000) {
            const re = /\d{1,3}(?=(\d{3})+$)/g;
            num1 = num.toString().replace(/^(\d+)((\.\d+)?)$/, function (s, s1, s2) {
                return s1.replace(re, '$&,') + s2;
            });
        }
        else {
            num1 = num.toString();
        }
        if (sign) {
            num1 = sign + num1;
        }
        if (cent > 0) {
            const rs = num1.split('.');
            let c;
            if (rs.length > 1) {
                c = rs[1].length;
            }
            else {
                c = 0;
                num1 += '.';
            }
            for (let i = c; i < cent; i++) {
                num1 += '0';
            }
        }
        return num1;
    },
};

/**
 * @description 输入内容的校验
 * @param value 需要校验的数值
 * @param option 配置需要校验的参数
 */
const writingFormat = (value, option) => {
    const wOption = Object.assign({
        decimals: 0,
        isNegative: true,
    }, option);
    value = String(value);
    let newValue;
    if (wOption.alphabet) {
        if (wOption.alphabet === 'lower') {
            // 只支持输入数字和小写字母
            newValue = value.replace(/[^a-z0-9]/g, '');
        }
        else if (wOption.alphabet === 'upper') {
            // 只支持输入数字和大写字母
            newValue = value.replace(/[^A-Z0-9]/g, '');
        }
        else {
            // 只能输入数字和字母（大小写字母）
            newValue = value.replace(/[\W]/g, '');
        }
    }
    else {
        // 支持输入数字
        newValue = value.replace(/[^\d.-]/g, '');
        // 是否支持负数
        if (wOption.isNegative) {
            newValue = newValue.replace(/-{2,}/g, '-').replace(/-\./g, '-');
            // 负号在不能数字之后
            if (newValue.indexOf('-') > 0) {
                newValue = newValue.replace(/-/g, '');
            }
            // 负号匹配
            newValue = newValue
                .replace('-', '$#$')
                .replace(/-/g, '')
                .replace('$#$', '-');
        }
        else {
            newValue = newValue.replace(/-/g, '');
        }
        // 小数点匹配
        if (wOption.decimals === 0) {
            newValue = newValue.replace(/\./g, '');
        }
        else {
            // 1. .replace('.', '$#$')把字符'.'替换成'$#$', 因为replace对于字符串只匹配一次, 所以只会替换第一个'.'
            // 2. .replace(/\./g, "")把其余的字符'.'替换为空字符串(删除)
            // 3. .replace("$#$", ".") 把字符'$#$'替换回原来的'.'
            newValue = newValue
                .replace(/\.{2,}/g, '.')
                .replace('.', '$#$')
                .replace(/\./g, '')
                .replace('$#$', '.');
            if (newValue.indexOf('.') != -1) {
                const len = newValue.split('.')[1].length;
                if (len > wOption.decimals) {
                    newValue = newValue.substring(0, newValue.length - (len - wOption.decimals));
                }
            }
        }
    }
    return newValue;
};

function cached(fn) {
    const cache = Object.create(null);
    return function cachedFn(str) {
        const hit = cache[str];
        return hit || (cache[str] = fn(str));
    };
}
const regex = /-(\w)/g;
const camelize = cached((str) => str.replace(regex, (_, c) => (c ? c.toUpperCase() : '')));
function removeNode(node) {
    if (node.parentElement !== null) {
        node.parentElement.removeChild(node);
    }
}
function insertNodeAt(fatherNode, node, position) {
    const refNode = position === 0
        ? fatherNode.children[0]
        : fatherNode.children[position - 1].nextSibling;
    fatherNode.insertBefore(node, refNode);
}
// 默认宽度转换
const BaseWidth = 1920; // 基准宽度
const transferWidth = (originWidth) => {
    if (window.innerWidth > BaseWidth)
        return (window.innerWidth / BaseWidth) * originWidth;
    return originWidth;
};

export { camelize, clickCopyListener, clickCopyListenerNew, copyListener, getQueryKeywords, getState, getUserInfo, globalEventListener, http, insertNodeAt, math, pasteListener, removeNode, removeState, setState, setlistValue, transferWidth, useInnerGrid, writingFormat };
